import type { TFunction } from 'i18next';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import { U2000_CRITERIA_LOOKUP_U3120_FIND_CUSTOMER } from '../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
import { NS } from '../U3120Constants';
import { u3120LanguageLabel, u3120RegularCustomerLabelKey } from './U3120CustomerDisplay';

export function u3120GetCustomerListColumns(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'customerCode',
      label: t('lblCustomer', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
      includeConditions: ['equals', 'in'],
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3120_FIND_CUSTOMER,
        searchCriterionField: 'search',
        valueKey: 'customerCode',
        displayKeys: ['customerCode', 'customerName'],
        placeholder: t('phCriteriaCustomer', { ns: 'common' }),
      },
    },
    {
      name: 'customerName',
      label: t('lblCustomerName', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'address',
      label: t('lblAddress', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'city',
      label: t('lblCity', { ns: NS }),
      widthClass: 'w-[150px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'postalCode',
      label: t('lblPostalCode', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'officePhone1',
      label: t('lblOfficePhonePrincipal1', { ns: NS }),
      widthClass: 'w-[150px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'officePhone2',
      label: t('lblOfficePhonePrincipal2', { ns: NS }),
      widthClass: 'w-[150px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
    },
    {
      name: 'isRegularCustomer',
      label: t('lblIsRegularCustomer', { ns: NS }),
      widthClass: 'w-[120px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value, _row) => {
        const key = u3120RegularCustomerLabelKey(value);
        return value ? (
          <span className="rounded-sm bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">{t(key, { ns: NS })}</span>
        ) : (
          <span className="rounded-sm bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">{t(key, { ns: NS })}</span>
        );
      },
    },
    {
      name: 'language',
      label: t('lblLanguage', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'string',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      sortable: true,
      isDefaultSortedColumn: false,
      display: (_column, value, _row) => {
        if (!value) return '';
        const lang: string = ((t as { i18n?: { language?: string } })?.i18n?.language as string) || 'fr';
        return <span className="rounded-sm bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">{u3120LanguageLabel(value, lang)}</span>;
      },
    },
    {
      name: 'createdAt',
      label: t('lblFldCreatedAt', { ns: NS }),
      widthClass: 'w-[80px]',
      isDefaultColumn: true,
      type: 'date',
      inGridField: true,
      sortable: true,
      includeConditions: ['equals', 'notequals', 'greaterthan', 'greaterthanorequal', 'lesthan', 'lesthanorequal', 'interval'],
      isCriteriaField: true,
    },
    {
      name: 'deletedAt',
      label: t('lblCndFieldDeletedAt', { ns: NS }),
      widthClass: 'w-[150px]',
      type: 'date',
      inGridField: true,
      align: 'center',
      display: (_column, value, _row) => {
        return value ? <span className="font-bold text-red-600">DELETED</span> : '';
      },
      includeConditions: ['isNotNull'],
      ovverideLabelConditions: [
        {
          name: 'isNotNull',
          label: t('lblCndDeletedAt', { ns: NS }),
        },
      ],
      isCriteriaField: true,
    },
  ];
}
